import { useCallback, useEffect, useState } from 'react';
import { localised, useI18n, type CopyKey } from './i18n';
import Curriculum from './Curriculum';
import Schedule from './Schedule';
import Research from './Research';
import Sources from './Sources';
import {
  closeObjective,
  createObjective,
  currentObjective,
  activateObjective,
  getCurriculum,
  startItemGeneration,
  isClosed,
  isError,
  listObjectives,
  reactivateObjective,
  startResearch,
  startSynthesis,
  updateTarget,
  type Objective,
} from './api';
import type { CurriculumNode, Locale, ObjectiveStatus } from '@nabu/shared';

/**
 * One objective at a time.
 *
 * Which is why this is not a form plus a list. Before there is an objective, the
 * screen asks for one; after, the screen *is* the objective — its generated name
 * as the heading, what the learner asked for underneath, and the target as the
 * one thing still adjustable. Closing it is what opens the next.
 *
 * The closed ones underneath are not an exception to that. They are the record of
 * what has been worked on, and the only way back into one — a learner who rotates
 * between two subjects should not have to retype an objective to return to it, and
 * without somewhere to see them, closing anything feels like deleting it.
 *
 * The target is saved on its own. Bundling it with the description meant moving
 * the slider resubmitted the description — and since the form cleared on save,
 * that failed with "description required", which is both wrong and baffling.
 */

/**
 * Errors that mean the list on screen no longer describes what is stored.
 *
 * All three are answered by reloading and saying so, rather than by retrying:
 * whichever objective he tapped, the decision was made against a list that has
 * since moved, and a silent retry would act on the same stale reading.
 */
const STALE_ERRORS = ['conflict', 'not_found', 'not_closed'];

/** The statuses in which a curriculum can exist, and so the ones worth asking for. */
const CURRICULUM_STATUSES: readonly ObjectiveStatus[] = ['review', 'active'];

/**
 * How long the screen is prepared to sit and wait.
 *
 * Sized for research, the longer of the two waits: five to fifteen minutes is
 * what a pass takes, so the ceiling has to sit past the top of that range rather
 * than in the middle of it, where it would give up on passes that were about to
 * land. Twenty minutes at a fifteen-second interval, which is nothing next to a
 * wait measured in minutes and cheap enough to leave running on a phone.
 *
 * It is bounded at all because the alternative is a tab left open overnight
 * reloading a finished objective every fifteen seconds, and because a pass that
 * is twenty minutes late is a real failure worth saying out loud rather than
 * hiding behind a spinner that never resolves.
 */
const POLL_ATTEMPTS = 80;
const POLL_INTERVAL_MS = 15_000;

/**
 * The generated title in the reader's language.
 *
 * The fallback itself lives in i18n.ts, because curriculum nodes need exactly the
 * same one and two of them would eventually disagree — see `localised`.
 */
function titleOf(objective: Objective, locale: Locale, fallback: string): string {
  return localised(objective.title, locale, fallback);
}

/**
 * Most recently closed first, which is not the order the API returns.
 *
 * That list is ordered by when each objective was *created*, which is right for
 * picking out the one just made and wrong here: something long-standing that was
 * shelved a minute ago would sort below newer objectives closed weeks earlier, in
 * a list whose every row is captioned with a closing date. The visible ordering
 * would contradict the visible dates.
 *
 * Rows closed before `closedAt` existed fall back to their creation date. That is
 * not a guess about when they closed — nothing knows that — it just keeps them in
 * a stable position among themselves instead of jumping to the top.
 */
function byMostRecentlyClosed(a: Objective, b: Objective): number {
  return (b.closedAt ?? b.createdAt).localeCompare(a.closedAt ?? a.createdAt);
}

/** The target control, shared by the create form and the live objective. */
function TargetFields({
  mode,
  setMode,
  score,
  setScore,
  date,
  setDate,
  disabled,
}: {
  mode: 'score' | 'date';
  setMode: (m: 'score' | 'date') => void;
  score: number;
  setScore: (n: number) => void;
  date: string;
  setDate: (s: string) => void;
  disabled: boolean;
}) {
  const { t } = useI18n();
  return (
    <fieldset className="field">
      <span>{t('obj.target.label')}</span>
      <div className="choices">
        {(['score', 'date'] as const).map((m) => (
          <label className="choice" key={m}>
            <input
              type="radio"
              name="mode"
              checked={mode === m}
              onChange={() => setMode(m)}
              disabled={disabled}
            />
            <span>{t(m === 'score' ? 'obj.target.score' : 'obj.target.date')}</span>
          </label>
        ))}
      </div>

      {mode === 'score' ? (
        <>
          <input
            type="range"
            min={70}
            max={97}
            value={score}
            onChange={(e) => setScore(Number(e.target.value))}
            disabled={disabled}
          />
          <small>
            {score}% — {t('obj.target.scoreHint')}
          </small>
        </>
      ) : (
        <>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            // Tomorrow at the earliest: the API refuses a past date, and offering
            // one in the picker only invites the error.
            min={new Date(Date.now() + 86_400_000).toISOString().slice(0, 10)}
            disabled={disabled}
            required
          />
          <small>{t('obj.target.dateHint')}</small>
        </>
      )}
    </fieldset>
  );
}

/**
 * What has been closed, and the way back into it.
 *
 * Rendered on both screens — with something live and without — because "switch to
 * another one" and "start again on an old one" are the same act from the learner's
 * side, and giving them two different places to look would hide whichever one he
 * happens not to be on.
 *
 * A disclosure rather than a list, and nothing at all when there is no history:
 * on a phone this sits under a screen that already scrolls, and a first objective
 * should not open beneath a box whose only content is that there is no history.
 */
function ClosedObjectives({
  items,
  live,
  busy,
  note,
  problem,
  open,
  onOpenChange,
  onReactivate,
}: {
  items: Objective[];
  live: Objective | null;
  busy: boolean;
  note?: string;
  problem?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReactivate: (objective: Objective) => void;
}) {
  const { t, locale } = useI18n();

  if (items.length === 0) return null;

  const closedOn = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  return (
    <section className="panel">
      <details className="history" open={open} onToggle={(e) => onOpenChange(e.currentTarget.open)}>
        <summary>
          <h2>{t('obj.history.title')}</h2>
          <span className="muted small">{items.length}</span>
        </summary>

        <p className="muted">{live ? t('obj.history.switchHint') : t('obj.history.hint')}</p>

        {problem && (
          <p className="problem" role="alert">
            {problem}
          </p>
        )}
        {note && (
          <p className="muted" role="status">
            {note}
          </p>
        )}

        <ul className="objectives">
          {items.map((objective) => (
            <li key={objective.objectiveId}>
              <h3>{titleOf(objective, locale, t('obj.history.untitled'))}</h3>
              <p className="meta">
                <span className={`pill pill-${objective.status}`}>
                  {t(`obj.status.${objective.status}` as CopyKey)}
                </span>{' '}
                {/* Dated from `closedAt` alone. Objectives closed before that field
                    existed have none, and dating them from `createdAt` would put a
                    confident wrong date on the one thing this row is here to say. */}
                {objective.closedAt && (
                  <>
                    {t('obj.history.closed')} {closedOn.format(new Date(objective.closedAt))}
                  </>
                )}
              </p>
              <div className="actions">
                <button
                  type="button"
                  className="button"
                  disabled={busy}
                  onClick={() => onReactivate(objective)}
                >
                  {t('obj.history.reactivate')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      </details>
    </section>
  );
}

export default function Objectives() {
  const { t, locale } = useI18n();
  const [objective, setObjective] = useState<Objective | null | undefined>();
  const [description, setDescription] = useState('');
  const [mode, setMode] = useState<'score' | 'date'>('score');
  const [score, setScore] = useState(85);
  const [date, setDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>();
  const [saved, setSaved] = useState(false);
  const [closed, setClosed] = useState<Objective[]>([]);
  // The history keeps its own two messages. Sharing `problem` with the form above
  // would report a failed reactivation next to the target slider, pinning the
  // complaint to a control that had nothing to do with it.
  const [closedProblem, setClosedProblem] = useState<string | undefined>();
  const [closedNote, setClosedNote] = useState<string | undefined>();
  /*
   * Whether the history is expanded, `undefined` until he says. It is held here
   * rather than inside the section because the two screens are different branches
   * of this render: bringing an objective back moves him from one to the other,
   * which unmounts the section, and state kept in there would take the expansion —
   * and the confirmation message inside it — down with it.
   */
  const [closedOpen, setClosedOpen] = useState<boolean>();
  /*
   * The curriculum, `undefined` until it has been asked for. Distinct from `[]`
   * on purpose: an empty array is "nothing built yet", which is what the offer to
   * build one is written against, and treating "not asked yet" as the same thing
   * would flash that offer under an objective that already has a plan.
   */
  const [nodes, setNodes] = useState<CurriculumNode[] | undefined>();
  /*
   * Generation has its own two messages rather than sharing the step panel's.
   *
   * It is the one action here that does not move the status, so it cannot be
   * reported by the status-driven panel above — and a batch runs for minutes after
   * the request returns, so its "started" note has to survive on its own.
   */
  const [itemsNote, setItemsNote] = useState<string | undefined>();
  const [itemsProblem, setItemsProblem] = useState<string | undefined>();
  const [writingItems, setWritingItems] = useState(false);
  const [starting_, setStarting_] = useState(false);
  /*
   * Starting has its own error, rather than borrowing the questions panel's.
   *
   * `itemsProblem` is rendered in two places — beside the questions and beside the
   * rebuild offer — so a failure from Start appeared twice, in two panels that had
   * nothing to do with it. One piece of state read in more than one place is not a
   * shared message, it is the same complaint pinned to unrelated controls.
   */
  const [startProblem, setStartProblem] = useState<string | undefined>();
  /*
   * A step has been asked for and the stored status has not caught up yet.
   *
   * This is what keeps the research button down. The API answers 202 the moment
   * the status is written, so the window between that answer and the screen
   * reading it back is exactly where a second press lands — and a second research
   * pass is the most expensive mistake this interface can make.
   */
  const [starting, setStarting] = useState(false);
  /*
   * The step's own complaint, kept apart from `problem` for the same reason the
   * history keeps its own: a refusal to start research reported next to the target
   * slider pins it on a control that had nothing to do with it.
   */
  const [stepProblem, setStepProblem] = useState<string | undefined>();
  const [waitedTooLong, setWaitedTooLong] = useState(false);
  /* Bumped to start another round of polling after this screen has given up. */
  const [pollRound, setPollRound] = useState(0);

  const load = useCallback(() => {
    void currentObjective().then((result) => {
      if (isError(result)) {
        setObjective(null);
        return;
      }
      setObjective(result.objective);
      // The controls start from what is actually stored, so the slider reflects
      // the objective rather than a default that silently disagrees with it.
      if (result.objective) {
        if (result.objective.target.mode === 'score') {
          setMode('score');
          setScore(Math.round(result.objective.target.targetScore * 100));
        } else {
          setMode('date');
          setDate(result.objective.target.targetDate.slice(0, 10));
        }
      }

      /*
       * The curriculum is read here, from the status that came back with it,
       * rather than by whatever renders it. Asking separately would mean asking
       * before knowing whether this objective could have one — a 404 for every
       * draft on screen, which is noise that buries the real ones.
       */
      if (result.objective && CURRICULUM_STATUSES.includes(result.objective.status)) {
        void getCurriculum(result.objective.objectiveId).then((curriculum) => {
          // An unreadable curriculum is shown as an absent one. There is nothing
          // different for the learner to do about either, and the alternative is
          // a complaint about a fetch beside a plan he can already read.
          setNodes(isError(curriculum) ? [] : curriculum.nodes);
        });
      } else {
        // Dropped rather than left standing: closing one objective and landing on
        // a draft would otherwise show the previous objective's plan beneath it.
        setNodes(undefined);
      }
    });

    /*
     * The closed ones come out of the full list, filtered here rather than asked
     * for separately. Which one is live stays the API's answer either way — this
     * screen only decides what counts as finished, and never promotes one of these
     * to live on its own reading.
     */
    void listObjectives().then((result) => {
      if (isError(result)) {
        setClosed([]);
        return;
      }
      setClosed(result.objectives.filter(isClosed).sort(byMostRecentlyClosed));
    });
  }, []);

  useEffect(load, [load]);

  const status = objective?.status;

  /*
   * Everything about a step in flight is cleared when the *status* changes, not
   * when the request returns.
   *
   * `starting` has to outlive its own request, or the button comes back for the
   * length of a reload. It must not outlive the status, though: research that
   * fails lands back on `drafting`, and a flag left set there would leave the
   * retry button dead for the rest of the session with no way to press it.
   */
  useEffect(() => {
    setStarting(false);
    setWaitedTooLong(false);
    setStepProblem(undefined);
  }, [status]);

  /*
   * Waiting on something that takes minutes.
   *
   * `starting` counts as waiting alongside the two statuses because the read that
   * would confirm them goes through a secondary index and is eventually
   * consistent: for a moment after the 202 the objective still comes back as it
   * was, and a poll that trusted only the status would not have started yet.
   */
  const waiting = starting || status === 'researching' || status === 'synthesising';

  useEffect(() => {
    if (!waiting) return;
    let stopped = false;

    /*
     * Polling, rather than pushing, for one event the learner is already sitting
     * in front of. There is no stop condition in the loop: a status that has
     * moved on takes `waiting` down with it, which tears this effect down, which
     * is the same mechanism as leaving the screen — one way for this to stop
     * rather than two that can disagree.
     */
    void (async () => {
      for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        if (stopped) return;
        load();
      }
      if (!stopped) setWaitedTooLong(true);
    })();

    // Without this the loop outlives the objective it was watching, and somebody
    // who closes one and states another gets two loops reloading the screen from
    // underneath him.
    return () => {
      stopped = true;
    };
  }, [waiting, load, pollRound]);

  const target = (): Objective['target'] =>
    mode === 'score'
      ? { mode: 'score', targetScore: score }
      : { mode: 'date', targetDate: date };

  function explain(error: string): string {
    if (error === 'invalid_target') return t('obj.error.target');
    if (error === 'description_required') return t('obj.error.description');
    if (error === 'sign_in_required') return t('obj.error.signIn');
    if (error === 'already_active') return t('obj.error.alreadyActive');
    if (error === 'not_found') return t('obj.error.notFound');
    if (error === 'not_closed') return t('obj.error.notClosed');
    if (error === 'conflict') return t('obj.error.conflict');
    if (error === 'not_ready') return t('items.error.notReady');
    if (error === 'already_running') return t('items.error.alreadyRunning');
    if (error === 'nothing_active') return t('start.error.nothingActive');
    if (error === 'no_items') return t('start.error.noItems');
    if (error === 'not_drafting') return t('obj.error.notDrafting');
    if (error === 'not_ready') return t('obj.error.notReady');
    if (error === 'no_material') return t('obj.error.noMaterial');
    return t('obj.error.generic');
  }

  async function create(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setProblem(undefined);

    const result = await createObjective(description, target());
    setBusy(false);
    if (isError(result)) {
      setProblem(explain(result.error));
      return;
    }
    // Not cleared here: `load` replaces the whole screen with the objective, so
    // there is no stale form left holding an empty description.
    load();
  }

  async function saveTarget() {
    if (!objective) return;
    setBusy(true);
    setProblem(undefined);
    setSaved(false);

    const result = await updateTarget(objective.objectiveId, target());
    setBusy(false);
    if (isError(result)) {
      setProblem(explain(result.error));
      return;
    }
    setSaved(true);
    load();
  }

  /**
   * Starts the research pass, once.
   *
   * `starting` is set before the request and deliberately not cleared when it
   * returns: the effect above clears it when the stored status moves, so there is
   * no instant at which an enabled button sits in front of a learner whose
   * research has already been paid for. Everything else in this file can afford a
   * double submit; this cannot.
   */
  async function research() {
    if (!objective) return;
    setStarting(true);
    setStepProblem(undefined);
    setWaitedTooLong(false);

    const result = await startResearch(objective.objectiveId);
    if (isError(result)) {
      setStarting(false);
      setStepProblem(explain(result.error));
      // Refused on the grounds of status, which means this screen was drawing
      // from a stale reading of it. Reloading answers that; retrying would put
      // the same question to the same stale reading.
      if (result.error === 'not_drafting') load();
      return;
    }
    load();
  }

  /** Builds the curriculum from whatever has been read so far. Valid from `review`. */
  async function synthesise() {
    if (!objective) return;
    setStarting(true);
    setStepProblem(undefined);
    setWaitedTooLong(false);

    const result = await startSynthesis(objective.objectiveId);
    if (isError(result)) {
      setStarting(false);
      setStepProblem(explain(result.error));
      // `no_material` is not stale — the objective really is in `review` with
      // nothing to build from — so the message stands and the screen is left
      // alone, with the offer to add a page of his own still under it.
      if (result.error === 'not_ready') load();
      return;
    }
    load();
  }

  /** Another round of polling, after this screen has stopped watching. */
  function checkAgain() {
    setWaitedTooLong(false);
    setPollRound((round) => round + 1);
    load();
  }

  /**
   * Brings a closed objective back, setting the live one aside if there is one.
   *
   * The confirmation is asked for only when something would actually be displaced.
   * A dialog on the other path would be asking him to agree to a consequence that
   * does not exist, which is how people learn to dismiss dialogs unread.
   */
  async function reactivate(target: Objective) {
    if (objective && !window.confirm(t('obj.history.confirm'))) return;

    setBusy(true);
    setClosedProblem(undefined);
    setClosedNote(undefined);

    const result = await reactivateObjective(target.objectiveId);
    setBusy(false);
    if (isError(result)) {
      setClosedProblem(explain(result.error));
      if (STALE_ERRORS.includes(result.error)) load();
      return;
    }

    // Whether anything was set aside is the server's answer, not a guess from what
    // this screen thought was live a moment ago.
    setClosedNote(result.shelved ? t('obj.history.switched') : t('obj.history.resumed'));
    // Held open past the switch so that message is read where it happened.
    setClosedOpen(true);
    // Cleared for the same reason `create` relies on: the description survives in
    // state while the objective screen covers the form, and reappears in it the
    // next time something is closed — text he typed weeks ago, presented as a draft.
    setDescription('');
    load();
  }

  async function close(status: 'done' | 'abandoned') {
    if (!objective) return;
    if (!window.confirm(t('obj.close.confirm'))) return;

    setBusy(true);
    setProblem(undefined);
    const result = await closeObjective(objective.objectiveId, status);
    setBusy(false);
    if (isError(result)) {
      setProblem(explain(result.error));
      return;
    }
    setDescription('');
    setScore(85);
    setDate('');
    setMode('score');
    load();
  }

  if (objective === undefined) {
    return (
      <section className="panel">
        <p className="muted">{t('obj.list.loading')}</p>
      </section>
    );
  }

  /*
   * The live objective is kept out of its own history by id, not only by status.
   * The list is read from a secondary index and is eventually consistent, so for a
   * moment after a switch the objective he just came back to can still be reported
   * as shelved — offering "pick this up again" on the objective already on screen.
   */
  const history = (
    <ClosedObjectives
      items={closed.filter((o) => o.objectiveId !== objective?.objectiveId)}
      live={objective}
      busy={busy}
      note={closedNote}
      problem={closedProblem}
      // Expanded by default only when nothing is live, because then this list is
      // half of what the screen is for; collapsed under a live objective, which is
      // already a long scroll on a phone. Either way his own last choice wins.
      open={closedOpen ?? !objective}
      onOpenChange={setClosedOpen}
      onReactivate={(target) => void reactivate(target)}
    />
  );

  // ---- No live objective: ask for one ----

  if (objective === null) {
    return (
      <>
        <section className="panel">
          <h2>{t('obj.new.title')}</h2>
          <form onSubmit={create} className="form">
            <label className="field">
              <span>{t('obj.description.label')}</span>
              <textarea
                rows={7}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('obj.description.placeholder')}
                maxLength={4000}
                disabled={busy}
                required
              />
              <small>{t('obj.description.hint')}</small>
            </label>

            <TargetFields
              mode={mode}
              setMode={setMode}
              score={score}
              setScore={setScore}
              date={date}
              setDate={setDate}
              disabled={busy}
            />

            {problem && (
              <p className="problem" role="alert">
                {problem}
              </p>
            )}

            <div className="actions">
              <button type="submit" className="button primary" disabled={busy}>
                {busy ? t('obj.working') : t('obj.new.submit')}
              </button>
            </div>
          </form>
        </section>
        {/* Rendered here as well as under a live objective: the schedule is the
            learner's own standing arrangement, and somebody about to state their
            first objective is exactly who wants to say when they can be reached. */}
        <Schedule />
        {history}
      </>
    );
  }

  // ---- A live objective: it is the screen ----

  const title = titleOf(objective, locale, t('obj.history.untitled'));

  const drafting = status === 'drafting';
  const researching = status === 'researching';
  const building = status === 'synthesising';
  const canBuild = status === 'review' && nodes?.length === 0;

  /**
   * Asks for the questions to be written.
   *
   * There is nothing to poll afterwards. Generation leaves the status where it is,
   * so the screen cannot watch a status change the way it watches research — and it
   * should not poll for minutes to hours either. The note says it is happening and
   * that closing the page is safe; `itemVersion` on the next natural load is what
   * confirms it landed.
   */
  async function writeItems() {
    if (!objective) return;
    setWritingItems(true);
    setItemsProblem(undefined);
    setItemsNote(undefined);

    const result = await startItemGeneration(objective.objectiveId);
    setWritingItems(false);
    if (isError(result)) {
      setItemsProblem(explain(result.error));
      // `not_ready` means this screen's idea of the curriculum is behind storage.
      if (result.error === 'not_ready') load();
      return;
    }
    setItemsNote(t('items.started'));
  }

  /**
   * Starts the loop.
   *
   * Confirmed, unlike every other action on this screen, because it is the only one
   * whose consequence lands somewhere other than this page — on a phone, repeatedly,
   * for weeks. That is worth one dialog.
   */
  async function start() {
    if (!objective) return;
    if (!window.confirm(t('start.confirm'))) return;

    setStarting_(true);
    setStartProblem(undefined);
    const result = await activateObjective(objective.objectiveId);
    setStarting_(false);
    if (isError(result)) {
      setStartProblem(explain(result.error));
      return;
    }
    // Reloaded rather than patched locally: activation changes the status, which
    // decides what this whole screen renders.
    load();
  }

  /*
   * The next step, in one panel, and only the step he is actually at.
   *
   * The lifecycle is a line — draft, research, review, running — and every status
   * has exactly one thing to do next. Showing the others greyed out would put two
   * steps he cannot take beside the one he can, and the greyed-out research
   * button in particular would invite the press this whole flow is built to
   * prevent. So each status renders its own step and nothing else.
   */
  const step = (drafting || researching || building || canBuild || stepProblem) && (
    <section className="panel">
      {drafting &&
        (objective.researchError ? (
          <>
            <h2>{t('obj.research.failed.title')}</h2>
            <p className="muted">{t('obj.research.failed.body')}</p>
            {/* The pipeline's own words, not a paraphrase. Research that died on
                an unreachable source and research that was throttled call for
                different next moves, and this string is the only thing that
                tells them apart. */}
            <p className="problem detail">{objective.researchError}</p>
          </>
        ) : (
          <>
            <h2>{t('obj.research.title')}</h2>
            <p className="muted">{t('obj.research.body')}</p>
          </>
        ))}

      {drafting && (
        <div className="actions">
          <button
            type="button"
            className="button primary"
            onClick={() => void research()}
            disabled={starting || busy}
          >
            {starting
              ? t('obj.research.starting')
              : objective.researchError
                ? t('obj.research.retry')
                : t('obj.research.start')}
          </button>
        </div>
      )}

      {(researching || building) && (
        <>
          <h2>{researching ? t('obj.research.running.title') : t('cur.building.title')}</h2>
          <p className="muted">
            {researching ? t('obj.research.running.body') : t('cur.building.body')}
          </p>
          {waitedTooLong && (
            <>
              <p className="muted">{t('obj.wait.slow')}</p>
              <div className="actions">
                <button type="button" className="button" onClick={checkAgain}>
                  {t('obj.wait.check')}
                </button>
              </div>
            </>
          )}
        </>
      )}

      {/*
       * The failure is reported whether or not there is a curriculum to show.
       *
       * It reads on its own rather than inside the build offer below, which only
       * appears when there are no nodes: a build that failed while an older
       * curriculum was still in place would otherwise be completely silent — the
       * outline would sit there looking current, and nothing anywhere would say
       * the attempt to replace it had failed.
       */}
      {status === 'review' && objective.synthesisError && (
        <>
          <h2>{t('cur.title')}</h2>
          <p className="muted">{t('cur.failed.body')}</p>
          <p className="problem detail">{objective.synthesisError}</p>
        </>
      )}

      {canBuild && (
        <>
          {!objective.synthesisError && (
            <>
              <h2>{t('cur.title')}</h2>
              <p className="muted">{t('cur.build.body')}</p>
            </>
          )}
          <div className="actions">
            <button
              type="button"
              className="button primary"
              onClick={() => void synthesise()}
              disabled={starting || busy}
            >
              {starting ? t('obj.working') : t('cur.build')}
            </button>
          </div>
        </>
      )}

      {stepProblem && (
        <p className="problem" role="alert">
          {stepProblem}
        </p>
      )}
    </section>
  );

  return (
    <>
      <section className="panel">
        <p className="meta">
          <span className="pill">{t(`obj.status.${objective.status}` as CopyKey)}</span>
        </p>
        <h2 className="objective-title">{title}</h2>

        <h3 className="label">{t('obj.current.description')}</h3>
        {/* Read-only, and shown rather than editable: it is the research prompt,
            and changing it after research has run would silently invalidate a
            curriculum built from the old wording. */}
        <p className="quoted">{objective.description}</p>
      </section>

      {/* Before the step panel deliberately: the decision in there — build a
          curriculum, or research again — is one this informs. */}
      <Research objectiveId={objective.objectiveId} />

      {step}

      {nodes && nodes.length > 0 && (
        <Curriculum objectiveId={objective.objectiveId} nodes={nodes} />
      )}

      {/*
       * Building it again, offered whenever there is a plan and it is still under
       * review.
       *
       * The offer used to require *zero* nodes, on the reasoning that a curriculum
       * only gets built once. That left no way out of a bad one: a pass that came
       * back in the wrong language put 69 nodes on the screen and simultaneously
       * removed the only button that could replace them, so the fix needed somebody
       * with database access. Synthesis reads the content store and replaces its own
       * previous output, so re-running is exactly what it is built for — and it costs
       * cents, because the expensive half is the research it does not repeat.
       */}
      {status === 'review' && nodes && nodes.length > 0 && (
        <section className="panel">
          <p className="muted">{t('cur.rebuild.body')}</p>
          {itemsProblem && (
            <p className="problem" role="alert">
              {itemsProblem}
            </p>
          )}
          <div className="actions">
            <button
              type="button"
              className="button"
              onClick={() => void synthesise()}
              disabled={starting || busy}
            >
              {starting ? t('obj.working') : t('cur.rebuild')}
            </button>
          </div>
        </section>
      )}

      {/*
       * The last gate before anything sends.
       *
       * Offered only when there is genuinely something to send: a plan, questions
       * written for it, and a status still under review. Showing it earlier would
       * offer a button whose only possible outcome is an error explaining what is
       * missing — which the screen already says, above, where the missing thing is.
       */}
      {status === 'review' && nodes && nodes.length > 0 && objective.itemVersion && (
        <section className="panel">
          <h2>{t('start.title')}</h2>
          <p className="muted">{t('start.body')}</p>
          {startProblem && (
            <p className="problem" role="alert">
              {startProblem}
            </p>
          )}
          <div className="actions">
            <button
              type="button"
              className="button primary"
              onClick={() => void start()}
              disabled={starting_ || busy}
            >
              {starting_ ? t('start.working') : t('start.button')}
            </button>
          </div>
        </section>
      )}

      {status === 'active' && (
        <section className="panel">
          <p className="meta">
            <span className="pill pill-extracted">{t('start.running')}</span>
          </p>
        </section>
      )}

      {/*
       * Offered only once there is a plan to write questions about, and shown on
       * `active` too — a running objective is exactly where somebody notices the
       * questions are thin and wants them rewritten.
       */}
      {nodes && nodes.length > 0 && (status === 'review' || status === 'active') && (
        <section className="panel">
          <h2>{t('items.title')}</h2>

          {objective.itemsError ? (
            <>
              <p className="muted">{t('items.failed.body')}</p>
              <p className="problem detail">{objective.itemsError}</p>
            </>
          ) : objective.itemVersion ? (
            <p className="meta">
              <span className="pill pill-extracted">{t('items.ready')}</span>{' '}
              {/* The pipeline's own count, not a number this screen worked out: it
                  knows how many it wrote, and a second opinion computed from a
                  paginated read is a second thing that can be wrong. */}
              {objective.itemsDetail}
            </p>
          ) : (
            <p className="muted">{t('items.build.body')}</p>
          )}

          {itemsProblem && (
            <p className="problem" role="alert">
              {itemsProblem}
            </p>
          )}
          {itemsNote && (
            <p className="muted" role="status">
              {itemsNote}
            </p>
          )}

          <div className="actions">
            <button
              type="button"
              className={objective.itemVersion ? 'button' : 'button primary'}
              onClick={() => void writeItems()}
              disabled={writingItems || busy}
            >
              {writingItems
                ? t('items.working')
                : objective.itemVersion
                  ? t('items.rebuild')
                  : t('items.build')}
            </button>
          </div>
        </section>
      )}

      {/* An objective that is running with nothing to run on. It should not
          happen, and saying so is better than a screen that quietly omits the
          plan the learning loop is supposedly working from. */}
      {status === 'active' && nodes?.length === 0 && (
        <section className="panel">
          <h2>{t('cur.title')}</h2>
          <p className="muted">{t('cur.empty')}</p>
        </section>
      )}

      <section className="panel">
        <TargetFields
          mode={mode}
          setMode={setMode}
          score={score}
          setScore={setScore}
          date={date}
          setDate={setDate}
          disabled={busy}
        />

        {problem && (
          <p className="problem" role="alert">
            {problem}
          </p>
        )}

        <div className="actions">
          <button type="button" className="button primary" onClick={saveTarget} disabled={busy}>
            {busy ? t('obj.working') : t('obj.target.save')}
          </button>
          {saved && <span className="muted">{t('obj.target.saved')}</span>}
        </div>
      </section>

      <Sources objectiveId={objective.objectiveId} />

      <Schedule />

      <section className="panel">
        <p className="muted">{t('obj.close.hint')}</p>
        <div className="actions">
          <button type="button" className="button" onClick={() => close('done')} disabled={busy}>
            {t('obj.close.done')}
          </button>
          <button type="button" className="button" onClick={() => close('abandoned')} disabled={busy}>
            {t('obj.close.abandon')}
          </button>
        </div>
      </section>

      {history}
    </>
  );
}
